import React, { useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../auth/AuthContext";
import { formatApiError } from "../api/client";

export default function LoginPage() {
  const { t } = useTranslation("auth");
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const from = (() => {
    // Only validated INTERNAL paths; plain login lands on "/" (not /dashboard).
    const p = location.state?.from?.pathname;
    return p && p.startsWith("/") && !p.startsWith("//") ? p : "/";
  })();
  // 2026-08 — allow the verify-replay page to pre-fill the email so
  // an already-verified user doesn't have to retype it. The email
  // itself is public info (they just typed it seconds ago) and we
  // never accept a password via query-string.
  const emailFromQuery = (searchParams.get("email") || "").trim();

  const [email, setEmail] = useState(emailFromQuery);
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(email.trim().toLowerCase(), password);
      // Phase 1 — public detail pages stash a return target before the
      // register/login handoff (e.g. /listing/{ulid}).
      let returnTo = null;
      try {
        returnTo = localStorage.getItem("da_return_to");
        if (returnTo) localStorage.removeItem("da_return_to");
        if (returnTo && (!returnTo.startsWith("/") || returnTo.startsWith("//"))) returnTo = null;
      } catch { /* private mode */ }
      navigate(returnTo || from, { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto max-w-md space-y-6" data-testid="login-page">
      <header className="space-y-2">
        <h1 className="text-3xl font-semibold tracking-tight">{t("login.heading")}</h1>
        <p className="text-sm text-zinc-400">
          {t("login.no_account")}{" "}
          <Link to="/register" className="text-emerald-400 underline-offset-4 hover:underline" data-testid="link-to-register">
            {t("login.create_one")}
          </Link>
          .
        </p>
      </header>

      <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
        <div className="space-y-1.5">
          <label htmlFor="login-email" className="text-xs uppercase tracking-[0.18em] text-zinc-500">
            {t("login.email")}
          </label>
          <input
            id="login-email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="login-email-input"
            className="w-full rounded-md border border-zinc-800 bg-zinc-900/60 px-3 py-2 text-sm text-zinc-100 placeholder-zinc-500 focus:border-emerald-500 focus:outline-none"
            placeholder={t("login.email_placeholder")}
          />
        </div>
        <div className="space-y-1.5">
          <label htmlFor="login-password" className="text-xs uppercase tracking-[0.18em] text-zinc-500">
            {t("login.password")}
          </label>
          <input
            id="login-password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="login-password-input"
            className="w-full rounded-md border border-zinc-800 bg-zinc-900/60 px-3 py-2 text-sm text-zinc-100 placeholder-zinc-500 focus:border-emerald-500 focus:outline-none"
          />
        </div>

        <div className="flex justify-end -mt-1">
          <Link
            to="/forgot-password"
            data-testid="link-to-forgot-password"
            className="text-xs text-zinc-400 underline-offset-4 hover:text-emerald-300 hover:underline"
          >
            Passwort vergessen?
          </Link>
        </div>

        {error && (
          <div
            data-testid="login-error"
            className="rounded-md border border-rose-900 bg-rose-950/40 px-3 py-2 text-sm text-rose-300"
          >
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          data-testid="login-submit"
          className="w-full rounded-md bg-emerald-500 px-4 py-2.5 text-sm font-medium text-zinc-950 transition-colors hover:bg-emerald-400 disabled:opacity-60"
        >
          {submitting ? t("login.submitting") : t("login.submit")}
        </button>
      </form>
    </div>
  );
}
